import { Box, Typography } from '@mui/material';
import React, { forwardRef, useImperativeHandle } from 'react';

import { useFetchData } from '../../hooks/useFetchData';
import { StyledSkeleton } from '../../theme/CustomComponents';
import { Event } from '../../Types/dealGPT';
import { EventSource, HistoryViewMode } from '../../Types/enums';
import { projectEventsPathUrl } from '../../util/routesUrls';
import HistoryEntry from './ViewModels/HistoryEntry';

export interface EventHistoryProps {
  entityType: EventSource;
  entityId: string;
  projectId?: string;
  viewMode: HistoryViewMode;
}

export interface EventHistoryRef {
  refetch: () => void;
}

const EventHistory = forwardRef<EventHistoryRef, EventHistoryProps>(
  ({ entityType, entityId, projectId, viewMode }, ref) => {
    const {
      data: events,
      loading,
      refetch,
    } = useFetchData<Event[]>({
      urlPath: projectEventsPathUrl(entityType, entityId, projectId),
    });

    useImperativeHandle(ref, () => ({
      refetch,
    }));

    return loading && !events ? (
      Array.from({ length: 3 }).map((_, index) => (
        <StyledSkeleton key={index} variant="rectangular" height={60} />
      ))
    ) : (
      <Box display="flex" flexDirection="column" pt={1}>
        {!events || events.length === 0 ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={4}>
            <Typography variant="body2" sx={{ color: '#656575' }}>
              No history available for this item.
            </Typography>
          </Box>
        ) : (
          events.map((event, idx) => (
            <HistoryEntry
              key={event.id}
              event={event}
              isLast={idx === events.length - 1}
              viewMode={viewMode}
            />
          ))
        )}
      </Box>
    );
  }
);

EventHistory.displayName = 'EventHistory';

export default EventHistory;
